/**
 * orgDocBloatGuard — proactive size guard for long-lived org-room Y.Docs.
 *
 * Why this exists
 * ────────────────
 * Org rooms are a WS-only, cold-seeded-from-Mongo read projection with no
 * IndexedDB cache (see orgYDocStore.ts's file header) — the server NEVER
 * persists their Y.Doc as a binary blob, and re-seeds a brand-new Y.Doc
 * from `dataspace_edges` + `entities` every time the room is evicted from
 * server memory (idle timeout, redeploy, Redis-based replica handoff).
 *
 * A browser tab, however, can stay connected for days without a refresh.
 * Every incidental churn op it applies — entity renames, ghost background-job
 * tombstones (see automationsStore.ts's `deleteNodeFromOrgYDoc` sweep),
 * pendingCmds add/delete for cross-tab commands like `billing_refresh` — is
 * its own top-level Y.js transaction. Y.js's garbage collector only reclaims
 * a deleted item's CONTENT, never the struct's identity (clientID/clock), so
 * the ENCODED SIZE of a long-lived doc's full update history grows without
 * bound even though its LIVE `nodes`+`edges` content stays tiny.
 *
 * The danger: when that tab reconnects to a server room that was re-seeded
 * in the meantime, the two Y.Docs share almost no CRDT history. Per the
 * y-protocols sync handshake, the client must answer the server's SyncStep1
 * with a SyncStep2 containing its ENTIRE accumulated history — not just the
 * live content — as ONE WebSocket frame. Confirmed in production
 * (org:69bb4cbf42fc3c6460077f89, a 9-person team, 2026-07): canonical live
 * content measured 0.73 MiB, but reconnect frames measured 16.9-17.8 MiB,
 * tripping the server's 16 MiB `org:` payload budget (`payloadBudget.js`)
 * and force-closing the connection with code 1008 — repeatedly, since the
 * tab's accumulated history doesn't shrink between attempts.
 *
 * The fix: self-heal (destroy + re-init the local Y.Doc from a clean cold
 * load) well before accumulated history could ever approach the server's
 * ceiling. This is safe specifically for org rooms because they have no
 * legitimate local-only state to lose (unlike `doc:`/`note:` rooms, where
 * the local Y.Doc IS the source of truth and destroying it would discard
 * unsynced edits).
 *
 * This module only detects; recovery (destroying and re-initializing the
 * store) stays in orgYDocStore.ts, which owns the room's lifecycle — same
 * split as wsOverflowRecovery.ts.
 */

import * as Y from 'yjs';

/**
 * Returns the fully-encoded size (v2 update format) of a Y.Doc's complete
 * state — its full structural history, not just live map/array entries.
 * This is exactly what a SyncStep2 reply carries on reconnect, so it's the
 * right proxy for "how big would this doc's reconnect frame be against a
 * peer that shares none of its history".
 */
export function getEncodedDocBytes(doc: Y.Doc): number {
	return Y.encodeStateAsUpdateV2(doc).byteLength;
}

/**
 * Pure decision: should this doc be proactively recycled before its
 * accumulated history risks tripping the server's payload budget on the
 * next reconnect?
 */
export function shouldSelfHealOrgDoc(doc: Y.Doc, thresholdBytes: number): boolean {
	return getEncodedDocBytes(doc) >= thresholdBytes;
}
